import { Component, computed, inject, input, signal, type TemplateRef } from "@angular/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faChartSimple } from "@fortawesome/free-solid-svg-icons";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { filter, switchMap, tap } from "rxjs";

import { PermissionActionDescriptorType } from "../../../../../adaa-types";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { CalculationDataType, PermissionAction } from "../../../constants/enums";
import { HasPermissionDirective } from "../../../directives";
import type {
  EntityPerformanceModelType,
  EntityPeriodFilterModel,
  GetCalculationValuesArgs,
  GovEnablerPillarModelType,
  SelectedPeriodModelType,
} from "../../../models";
import { CalculationEngineApiService, GovEnablerPerfService, PermissionsService } from "../../../services";
import { DoughnutComponent } from "../../charts";
import { EntityComparisonModalComponent } from "../entity-comparison-modal/entity-comparison-modal";
import { GovEnablerPerfCardComponent } from "../gov-enabler-perf-card/gov-enabler-perf-card";
import { ItemSliderComponent } from "../item-slider/item-slider";

@Component({
  selector: "adaa-gov-enabler-performance",
  templateUrl: "./gov-enabler-performance.html",
  styleUrl: "./gov-enabler-performance.scss",
  imports: [
    DoughnutComponent,
    TranslatePipe,
    FontAwesomeModule,
    GovEnablerPerfCardComponent,
    ItemSliderComponent,
    EntityComparisonModalComponent,
    HasPermissionDirective,
  ],
})
export class GovEnablerPerformanceComponent {
  private readonly _modalService = inject(NgbModal);
  private readonly _govEnablerPerfService = inject(GovEnablerPerfService);
  private readonly _calculationEngineApiService = inject(CalculationEngineApiService);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _translateService = inject(TranslateService);

  selectedPeriod = input<SelectedPeriodModelType>();

  entitiesPerformance = signal<EntityPerformanceModelType[]>([]);
  entityPeriodFilter = signal<EntityPeriodFilterModel | undefined>(undefined);
  averagePerformance = signal<number | undefined>(undefined);
  selectedGovEnablerPerf = signal<GovEnablerPillarModelType | undefined>(undefined);

  readonly performancePermission: PermissionActionDescriptorType = {
    actor: {
      modifier: "or",
      permissions: [
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.PMO_HOMEPAGE_ENABLER_OVERALL,
        },
      ],
    },
  };

  readonly pillarPermission: PermissionActionDescriptorType = {
    actor: {
      modifier: "or",
      permissions: [
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.PMO_HOMEPAGE_ENABLER_PILLARS,
        },
      ],
    },
  };

  readonly graphIcon = faChartSimple;
  readonly defaultColorId = Constants.CONSTANT_COLORS.EVALUATION_GRAY;

  showEntityTypeAndSize = computed(() => {
    if (this.entityPeriodFilter()) return this.entityPeriodFilter()?.periodType === "year";
    return this.selectedPeriod()?.type === "year";
  });
  pillars = computed(() => this._govEnablerPerfService.pillars());
  overallPillar = computed(() => this._govEnablerPerfService.overallPillar());

  overallColorId = computed(() =>
    this._evaluationColorId(
      this.overallPillar()?.govEnablerPerformance,
      this.overallPillar()?.govEnablerPerformanceColor
    )
  );

  periodLabel = computed(() => {
    const period = this.selectedPeriod();
    if (!period?.period) return "";
    return period.type === "year" ? `${period.period.year}` : `Q${period.period.quarter} ${period.period.year}`;
  });

  showOverall = computed(() => {
    if (!this.overallPillar()) return false;

    const { permissions, modifier } = this.performancePermission.actor;
    return this._permissionsService.hasPermission(permissions, modifier);
  });

  performanceCsvRows = computed(() =>
    this.entitiesPerformance().map((data) => [
      AdaaHelper.getItemValueByToken(data, "entityName"),
      `${data.score ?? 0}`,
      `${this.averagePerformance() ?? ""}`,
    ])
  );

  public get chartTitle() {
    if (this.selectedGovEnablerPerf()) return AdaaHelper.getItemValueByToken(this.selectedGovEnablerPerf(), "name");
    return this._translateService.instant("landingPage.entity_page.government_enabler_performance");
  }

  public getColor(code: number) {
    return Constants.COLOR_CODES[code];
  }

  public onFilterChange(event: EntityPeriodFilterModel) {
    this.entityPeriodFilter.update(() => event);

    if (!this.selectedGovEnablerPerf()) {
      this._fetchPerformanceData(this.overallPillar()!.govEnablerAggregationType);
    } else {
      this._fetchPerformanceData(this.selectedGovEnablerPerf()!.id);
    }
  }

  public openPerformanceGraph(content: TemplateRef<unknown>) {
    // Always reset the overridden period when opening the modal
    this.entityPeriodFilter.update(() => undefined);

    this._modalService.open(content, {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "xl",
      centered: true,
      fullscreen: false,
      ariaLabelledBy: this.chartTitle,
    });

    if (!this.selectedGovEnablerPerf()) {
      this._fetchPerformanceData(this.overallPillar()!.govEnablerAggregationType);
    } else {
      this._fetchPerformanceData(this.selectedGovEnablerPerf()!.id);
    }
  }

  private _evaluationColorId(value: number | undefined, colorCode: number | undefined) {
    if (!AdaaHelper.isDefined(value) || !AdaaHelper.isDefined(colorCode)) return this.defaultColorId;
    return colorCode;
  }

  private _fetchPerformanceData(id: number) {
    let periodId = this.selectedPeriod()?.type === "quarter" ? this.selectedPeriod()?.period?.id : undefined;
    let year = this.selectedPeriod()?.type === "year" ? this.selectedPeriod()?.period?.year : undefined;

    if (this.entityPeriodFilter()?.period) {
      periodId = this.entityPeriodFilter()?.periodType === "quarter" ? this.entityPeriodFilter()?.period : undefined;
      year = this.entityPeriodFilter()?.periodType === "year" ? this.entityPeriodFilter()?.period : undefined;
    }

    const params = {
      type: CalculationDataType.ENTITY,
      governmentAggregationType: this.selectedGovEnablerPerf() ? undefined : id,
      pillarId: this.selectedGovEnablerPerf() ? id : undefined,
      year: year,
      periodId: periodId,
      entitySize: this.entityPeriodFilter()?.entitySize,
      entityType: this.entityPeriodFilter()?.entityType,
    };

    this._calculationEngineApiService
      .getCalculationData(params)
      .pipe(
        tap((res) => {
          if (res.inError) return;
          this.entitiesPerformance.update(() => res.responseData as EntityPerformanceModelType[]);
        }),
        switchMap(() => this._getAveragePerformance(params)),
        filter((res) => AdaaHelper.isDefined(res)),
        tap((res) => {
          if (res.inError) return;
          const score = (res.responseData as { score: number })?.score || undefined;
          this.averagePerformance.update(() => score);
        })
      )
      .subscribe();
  }

  private _getAveragePerformance(params: GetCalculationValuesArgs) {
    return this._calculationEngineApiService.getCalculationData({
      ...params,
      type: this.selectedGovEnablerPerf() ? CalculationDataType.GOVERNMENT_PILLARS : CalculationDataType.GOVERNMENT,
    });
  }
}
